export function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

export function timeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  const diffMonths = Math.floor(diffDays / 30);
  return `${diffMonths}mo ago`;
}

export function cn(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(" ");
}

export function shareToSocial(
  platform: "instagram" | "facebook" | "tiktok" | "pinterest" | "x",
  url: string,
  text: string,
  imageUrl?: string
) {
  const encodedUrl = encodeURIComponent(url);
  const encodedText = encodeURIComponent(text);

  const urls: Record<string, string> = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    x: `https://x.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
    pinterest: `https://pinterest.com/pin/create/button/?url=${encodedUrl}&description=${encodedText}${imageUrl ? `&media=${encodeURIComponent(imageUrl)}` : ""}`,
    tiktok: `https://www.tiktok.com/share?url=${encodedUrl}`,
    instagram: url, // Instagram doesn't have a direct share URL; fallback to copy
  };

  if (platform === "instagram") {
    navigator.clipboard.writeText(url);
    return;
  }

  window.open(urls[platform], "_blank", "width=600,height=400,noopener,noreferrer");
}

// App store links
export const APP_STORE_URL = "https://apps.apple.com/us/app/quuil/id6757066434";
export const PLAY_STORE_URL = "#";

// --- Form validation ---

export interface ValidationResult {
  valid: boolean;
  errors: Record<string, string>;
}

export function validateEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function validateRequired(value: string, fieldName: string): string | null {
  if (!value.trim()) return `${fieldName} is required`;
  return null;
}

export function validateShippingForm(data: {
  email: string;
  firstName: string;
  lastName: string;
  address1: string;
  city: string;
  state: string;
  zip: string;
}): ValidationResult {
  const errors: Record<string, string> = {};

  const emailErr = validateRequired(data.email, "Email");
  if (emailErr) {
    errors["email"] = emailErr;
  } else if (!validateEmail(data.email)) {
    errors["email"] = "Please enter a valid email address";
  }

  const checks: [string, string, string][] = [
    [data.firstName, "firstName", "First name"],
    [data.lastName, "lastName", "Last name"],
    [data.address1, "address1", "Address"],
    [data.city, "city", "City"],
    [data.state, "state", "State"],
    [data.zip, "zip", "ZIP code"],
  ];

  for (const [value, key, label] of checks) {
    const err = validateRequired(value, label);
    if (err) errors[key] = err;
  }

  return { valid: Object.keys(errors).length === 0, errors };
}

export function validateQuoteForm(data: {
  companyName: string;
  contactName: string;
  email: string;
  products: Array<{ product_type: string; quantity: string }>;
}): ValidationResult {
  const errors: Record<string, string> = {};

  const companyErr = validateRequired(data.companyName, "Company name");
  if (companyErr) errors["companyName"] = companyErr;

  const contactErr = validateRequired(data.contactName, "Contact name");
  if (contactErr) errors["contactName"] = contactErr;

  const emailErr = validateRequired(data.email, "Email");
  if (emailErr) {
    errors["email"] = emailErr;
  } else if (!validateEmail(data.email)) {
    errors["email"] = "Please enter a valid email address";
  }

  data.products.forEach((p, i) => {
    if (!p.product_type) errors[`product_${i}_type`] = "Product type is required";
    if (!p.quantity || parseInt(p.quantity) < 1) {
      errors[`product_${i}_quantity`] = "Quantity must be at least 1";
    }
  });

  return { valid: Object.keys(errors).length === 0, errors };
}

/**
 * Sanitize user input for display — strip HTML tags.
 * Not a substitute for server-side sanitization but prevents basic XSS in client rendering.
 */
export function sanitizeInput(input: string): string {
  const div = document.createElement("div");
  div.textContent = input;
  return div.innerHTML;
}
